/**
 * KOPS-559: Sponsor/Lender Signup — Role-based pricing with add-ons
 * Route: /sponsor/signup
 * Flow: Select products → add-ons → company info → Stripe checkout → account created
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faChartLine, faRobot, faShieldAlt, faCheckCircle, faCreditCard, faArrowRight, faDatabase, faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { colors, fonts, FONT_LINK_HREF } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';

const isKairosIQDomain = typeof window !== 'undefined' && (
  window.location.hostname.includes('app.kairosiq.ai') ||
  window.location.hostname.includes('demo.kairosiq.ai') ||
  window.location.hostname.includes('dev.app.kairosiq.ai') ||
  new URLSearchParams(window.location.search).get('brand') === 'kairosiq'
);

const SponsorSignupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success');

  const [step, setStep] = useState<'plan' | 'info'>(success ? 'info' : 'plan');
  const roleParam = searchParams.get('role');
  const [role, setRole] = useState<'lender' | 'broker'>(roleParam === 'broker' ? 'broker' : 'lender');
  const [form, setForm] = useState({
    company: '', contactName: '', email: '', phone: '',
    sponsorType: roleParam === 'broker' ? 'broker' : 'lender', password: '', confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);
  const [respaAck, setRespaAck] = useState(false);
  const [termsAck, setTermsAck] = useState(false);

  // Lender product selections
  const [pipeline, setPipeline] = useState(role === 'lender');
  const [pipelineAdmin, setPipelineAdmin] = useState(false);
  const [adminCount, setAdminCount] = useState(1);
  const [coaching, setCoaching] = useState(role === 'lender');
  const [dataMigration, setDataMigration] = useState(false);
  const [agentOnboarding, setAgentOnboarding] = useState(false);
  const [agentCount, setAgentCount] = useState(10);

  // Broker product selections
  const [brokerWebsite, setBrokerWebsite] = useState(role === 'broker');
  const [brokerCoaching, setBrokerCoaching] = useState(false);
  const [brokerCompliance, setBrokerCompliance] = useState(false);
  const [brokerAgentSeats, setBrokerAgentSeats] = useState(role === 'broker');
  const [brokerSeatCount, setBrokerSeatCount] = useState(10);

  // Calculate totals based on role
  const monthlyTotal = role === 'broker'
    ? (brokerWebsite ? 500 : 0) + (brokerCompliance ? 500 : (brokerCoaching ? 250 : 0)) + (brokerAgentSeats ? brokerSeatCount * 100 : 0)
    : (pipeline ? 500 : 0) + (pipelineAdmin ? 250 * adminCount : 0) + (coaching ? 500 : 0);
  const oneTimeTotal = (dataMigration ? 1500 : 0) + (agentOnboarding ? agentCount * 150 : 0);

  // After Stripe redirects back with success, create the account and auto-login
  React.useEffect(() => {
    if (success) {
      const sessionId = searchParams.get('session_id');
      const signupData = JSON.parse(localStorage.getItem('sponsor_signup') || '{}');
      if (sessionId && !accountCreated) {
        setAccountCreated(true);
        (async () => {
          try {
            const { data: completeData } = await axiosInstance.post('/portal-stripe/sponsor-complete', {
              sessionId, password: signupData.password, company: signupData.company,
              contactName: signupData.contactName, phone: signupData.phone,
              sponsorType: signupData.sponsorType,
            });
            if (signupData.email && signupData.password) {
              const loginRes = await axiosInstance.post('/agents/login', { email: signupData.email, password: signupData.password });
              if (loginRes.data.token) {
                localStorage.setItem('auth_token', loginRes.data.token);
                localStorage.setItem('agent_token', loginRes.data.token);
                if (loginRes.data.user) localStorage.setItem('user_info', JSON.stringify(loginRes.data.user));
                localStorage.removeItem('sponsor_signup');
                window.location.href = '/sponsor/dashboard';
                return;
              }
            }
          } catch (err: any) {
            console.error('[SPONSOR] Error:', err?.response?.data || err?.message);
          }
          localStorage.removeItem('sponsor_signup');
          setTimeout(() => { window.location.href = '/sign-in'; }, 3000);
        })();
      }
    }
  }, [success]);

  const handleCheckout = async () => {
    if (!form.company || !form.email || !form.contactName) {
      setError('Company name, contact name, and email are required');
      return;
    }
    if (!form.password || form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (monthlyTotal === 0) {
      setError('Please select at least one product');
      return;
    }
    if (!respaAck) {
      setError('Please acknowledge the RESPA compliance terms');
      return;
    }
    if (!termsAck) {
      setError('Please accept the Terms of Service');
      return;
    }
    setLoading(true);
    setError('');
    try {
      localStorage.setItem('sponsor_signup', JSON.stringify({
        company: form.company, contactName: form.contactName, email: form.email,
        phone: form.phone, sponsorType: form.sponsorType, password: form.password, role,
        products: role === 'broker'
          ? { brokerWebsite, brokerCoaching, brokerCompliance, brokerAgentSeats, brokerSeatCount }
          : { pipeline, pipelineAdmin, coaching, dataMigration, agentOnboarding, agentCount },
      }));

      const { data } = await axiosInstance.post('/portal-stripe/create-checkout', {
        plan: role === 'broker' ? 'broker-platform' : 'sponsor-seats',
        email: form.email,
        name: form.contactName,
        company: form.company,
        sponsorType: form.sponsorType,
        role,
        // Lender products
        pipeline: role === 'lender' ? pipeline : false,
        pipelineAdmin: role === 'lender' ? pipelineAdmin : false,
        adminCount: role === 'lender' ? adminCount : 0,
        coaching: role === 'lender' ? coaching : false,
        dataMigration,
        agentOnboarding: role === 'lender' ? agentOnboarding : false,
        agentOnboardingCount: role === 'lender' && agentOnboarding ? agentCount : 0,
        // Broker products
        brokerWebsite: role === 'broker' ? brokerWebsite : false,
        brokerCoaching: role === 'broker' ? brokerCoaching : false,
        brokerCompliance: role === 'broker' ? brokerCompliance : false,
        brokerAgentSeats: role === 'broker' ? brokerAgentSeats : false,
        brokerSeatCount: role === 'broker' && brokerAgentSeats ? brokerSeatCount : 0,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Payment system error. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div style={{ background: '#f8f9fa', minHeight: '100vh', fontFamily: fonts.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <link href={FONT_LINK_HREF} rel="stylesheet" />
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 500, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 48, color: '#27ae60', marginBottom: 16 }} />
          <h2 style={{ fontFamily: fonts.display, fontSize: 24, color: colors.navy, margin: '0 0 12px' }}>Setting up your account...</h2>
          <p style={{ fontSize: 15, color: '#555', lineHeight: 1.6, marginBottom: 24 }}>
            Payment received. Creating your dashboard and redirecting you now.
          </p>
          <div style={{ fontSize: 13, color: '#888' }}>
            If you're not redirected in 10 seconds, <a href="/sign-in" style={{ color: colors.gold, fontWeight: 600 }}>click here to sign in</a>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh', fontFamily: fonts.body }}>
      <link href={FONT_LINK_HREF} rel="stylesheet" />

      {/* Header */}
      <div style={{ background: colors.navy, padding: '24px 40px', textAlign: 'center', borderBottom: `3px solid ${colors.gold}` }}>
        <h1 style={{ fontFamily: fonts.display, fontSize: 28, color: '#fff', margin: 0, letterSpacing: 2 }}>
          {isKairosIQDomain ? <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span></> : <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal</>}
        </h1>
        <p style={{ color: '#8FA3BF', fontSize: 13, margin: '4px 0 0' }}>
          {isKairosIQDomain ? 'AI-Powered Intelligence Platform' : 'KairosIQ AI, Inc.'}
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 24px' }}>
        <h2 style={{ fontFamily: fonts.display, fontSize: 32, color: colors.navy, textAlign: 'center', margin: '0 0 8px' }}>
          {role === 'broker' ? 'Get Started — Brokerage' : 'Get Started — Lender'}
        </h2>
        <p style={{ textAlign: 'center', fontSize: 15, color: '#666', margin: '0 0 24px' }}>
          Select the products for your business.
        </p>

        {/* Role Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 0, marginBottom: 32, background: '#f0f0f0', borderRadius: 10, padding: 4, maxWidth: 400, margin: '0 auto 32px' }}>
          {([['lender', 'Lender / Title'], ['broker', 'Broker / Brokerage']] as const).map(([r, label]) => (
            <button key={r} onClick={() => { setRole(r); setForm(f => ({ ...f, sponsorType: r })); }}
              style={{
                flex: 1, padding: '12px 20px', fontSize: 14, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer',
                background: role === r ? colors.navy : 'transparent',
                color: role === r ? '#fff' : '#666',
                transition: 'all 0.2s',
              }}>
              {label}
            </button>
          ))}
        </div>

        {step === 'plan' && (
          <>
            {/* Broker Products */}
            {role === 'broker' && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                  Brokerage Products — Monthly
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {[
                    { id: 'website', label: 'AI-Powered Website', price: '$500/mo', desc: 'Custom branded website with AI search, lead routing, agent directory, seller lead gen', icon: faChartLine, selected: brokerWebsite, toggle: () => setBrokerWebsite(!brokerWebsite) },
                    { id: 'coaching', label: 'AI Coaching Console', price: '$250/mo', desc: 'Agent production metrics, coaching prompts, retention intelligence, weekly planner', icon: faRobot, selected: brokerCoaching && !brokerCompliance, toggle: () => { setBrokerCoaching(!brokerCoaching); if (brokerCompliance) setBrokerCompliance(false); } },
                    { id: 'compliance', label: 'Compliance Suite', price: '$500/mo', desc: 'Coaching included + Ask the Broker AI + document review + Fair Housing audit + NAR compliance', icon: faShieldAlt, selected: brokerCompliance, toggle: () => { setBrokerCompliance(!brokerCompliance); if (!brokerCompliance) setBrokerCoaching(false); } },
                  ].map((p) => (
                    <div key={p.id} onClick={p.toggle} style={{
                      background: p.selected ? '#FFFAF0' : '#fff', borderRadius: 12, padding: 20, cursor: 'pointer',
                      border: p.selected ? `2px solid ${colors.gold}` : '2px solid #e9ecef',
                      boxShadow: p.selected ? '0 4px 16px rgba(201,149,42,0.15)' : 'none',
                      transition: 'all 0.2s',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <FontAwesomeIcon icon={p.icon} style={{ color: p.selected ? colors.gold : '#ccc', fontSize: 18 }} />
                        <div style={{
                          width: 22, height: 22, borderRadius: 6,
                          border: p.selected ? `2px solid ${colors.gold}` : '2px solid #ccc',
                          background: p.selected ? colors.gold : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {p.selected && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#fff', fontSize: 14 }} />}
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: colors.navy, marginBottom: 4 }}>{p.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: colors.navy }}>{p.price}</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4, lineHeight: 1.4 }}>{p.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Agent Seats */}
                <div onClick={() => setBrokerAgentSeats(!brokerAgentSeats)} style={{
                  background: brokerAgentSeats ? '#FFFAF0' : '#fff', borderRadius: 12, padding: 20, cursor: 'pointer',
                  border: brokerAgentSeats ? `2px solid ${colors.gold}` : '2px solid #e9ecef', marginTop: 12,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: brokerAgentSeats ? `2px solid ${colors.gold}` : '2px solid #ccc',
                      background: brokerAgentSeats ? colors.gold : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {brokerAgentSeats && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#fff', fontSize: 14 }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: colors.navy }}>
                        <FontAwesomeIcon icon={faUsers} style={{ color: colors.gold, marginRight: 6 }} />
                        Agent Seats — Full AI Platform per Agent
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>AI buyer search & scoring, seller retention (22 touchpoints), home reports, personal portal page</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: colors.navy }}>$100<span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>/seat/mo</span></div>
                  </div>
                </div>
                {brokerAgentSeats && (
                  <div style={{ background: '#fff', borderRadius: 8, padding: 12, marginTop: 8, border: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 12 }} onClick={e => e.stopPropagation()}>
                    <span style={{ fontSize: 12, color: '#555' }}>How many agents?</span>
                    <input type="number" min="1" max="500" value={brokerSeatCount}
                      onChange={e => setBrokerSeatCount(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ width: 70, padding: '6px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #ddd', textAlign: 'center', outline: 'none' }}
                    />
                    <span style={{ fontSize: 12, color: colors.gold, fontWeight: 600 }}>= ${(brokerSeatCount * 100).toLocaleString()}/mo</span>
                  </div>
                )}
              </div>
            )}

            {/* Lender Products */}
            {role === 'lender' && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                Core Products — Monthly
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { id: 'pipeline', label: 'Pipeline Manager', price: '$500/mo', desc: 'Kanban board, deal tracking, lead management', icon: faChartLine, selected: pipeline, toggle: () => setPipeline(!pipeline) },
                  { id: 'admin', label: 'Pipeline Admin', price: `$250/seat/mo`, desc: 'Processor seats — manage pipeline, notes, stages', icon: faUsers, selected: pipelineAdmin, toggle: () => setPipelineAdmin(!pipelineAdmin) },
                  { id: 'coaching', label: 'AI Coaching Console', price: '$500/mo', desc: 'Agent intelligence, coaching prompts, weekly planner. 4 admin seats included.', icon: faRobot, selected: coaching, toggle: () => setCoaching(!coaching) },
                ].map((p) => (
                  <div key={p.id} onClick={p.toggle} style={{
                    background: p.selected ? '#FFFAF0' : '#fff', borderRadius: 12, padding: 20, cursor: 'pointer',
                    border: p.selected ? `2px solid ${colors.gold}` : '2px solid #e9ecef',
                    boxShadow: p.selected ? '0 4px 16px rgba(201,149,42,0.15)' : 'none',
                    transition: 'all 0.2s',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <FontAwesomeIcon icon={p.icon} style={{ color: p.selected ? colors.gold : '#ccc', fontSize: 18 }} />
                      <div style={{
                        width: 22, height: 22, borderRadius: 6,
                        border: p.selected ? `2px solid ${colors.gold}` : '2px solid #ccc',
                        background: p.selected ? colors.gold : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {p.selected && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#fff', fontSize: 14 }} />}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: colors.navy, marginBottom: 4 }}>{p.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: colors.navy }}>{p.price}</div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4, lineHeight: 1.4 }}>{p.desc}</div>
                  </div>
                ))}
              </div>

              {/* Seat selectors — show below cards when selected */}
              {pipelineAdmin && (
                <div style={{ background: '#fff', borderRadius: 8, padding: 12, marginTop: 8, border: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 12, color: '#555' }}>Admin seats:</span>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setAdminCount(n)} style={{
                      width: 32, height: 32, borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                      border: adminCount === n ? `2px solid ${colors.gold}` : '1px solid #ddd',
                      background: adminCount === n ? colors.gold : '#fff',
                      color: adminCount === n ? '#fff' : '#555',
                    }}>{n}</button>
                  ))}
                  <span style={{ fontSize: 12, color: colors.gold, fontWeight: 600 }}>= ${(adminCount * 250).toLocaleString()}/mo</span>
                </div>
              )}
            </div>
            )}

            {/* Add-ons — Lender only */}
            {role === 'lender' && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: colors.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
                Add-Ons — One-Time
              </div>

              {/* Data Migration */}
              <div onClick={() => setDataMigration(!dataMigration)} style={{
                background: dataMigration ? '#FFFAF0' : '#fff', borderRadius: 12, padding: 20, cursor: 'pointer',
                border: dataMigration ? `2px solid ${colors.gold}` : '2px solid #e9ecef',
                marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 6,
                    border: dataMigration ? `2px solid ${colors.gold}` : '2px solid #ccc',
                    background: dataMigration ? colors.gold : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {dataMigration && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#fff', fontSize: 14 }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: colors.navy }}>
                      <FontAwesomeIcon icon={faDatabase} style={{ color: colors.gold, marginRight: 6 }} />
                      Full Data Migration
                      <span style={{ fontSize: 10, fontWeight: 600, background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: 4, marginLeft: 8 }}>RECOMMENDED</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>We import your entire book of business — leads, borrowers, deal history. 30-day implementation with dedicated support.</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: colors.navy }}>$1,500</div>
                  <div style={{ fontSize: 10, color: '#888' }}>one-time</div>
                </div>
              </div>

              {/* Agent Onboarding */}
              <div onClick={() => setAgentOnboarding(!agentOnboarding)} style={{
                background: agentOnboarding ? '#FFFAF0' : '#fff', borderRadius: 12, padding: 20, cursor: 'pointer',
                border: agentOnboarding ? `2px solid ${colors.gold}` : '2px solid #e9ecef',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6,
                      border: agentOnboarding ? `2px solid ${colors.gold}` : '2px solid #ccc',
                      background: agentOnboarding ? colors.gold : '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {agentOnboarding && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#fff', fontSize: 14 }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: colors.navy }}>
                        <FontAwesomeIcon icon={faUserPlus} style={{ color: colors.gold, marginRight: 6 }} />
                        Agent Onboarding — Classify Their Book of Business
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>AI classifies each agent's CRM, drafts re-engagement emails, builds their pipeline. Agents get 30 days free on the platform.</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: colors.navy }}>$150<span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>/agent</span></div>
                    <div style={{ fontSize: 10, color: '#888' }}>one-time</div>
                  </div>
                </div>
                {agentOnboarding && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <span style={{ fontSize: 12, color: '#555' }}>How many agents?</span>
                      <input type="number" min="1" max="100" value={agentCount}
                        onChange={e => setAgentCount(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ width: 70, padding: '6px 10px', fontSize: 14, borderRadius: 6, border: '1px solid #ddd', textAlign: 'center', outline: 'none' }}
                      />
                      <span style={{ fontSize: 12, color: colors.gold, fontWeight: 600 }}>= ${(agentCount * 150).toLocaleString()}</span>
                    </div>
                    <div style={{ fontSize: 10, color: '#555', background: '#f0f7ff', borderRadius: 6, padding: '8px 10px', lineHeight: 1.6 }}>
                      <strong>Note:</strong> Agents must opt in to share their production data for coaching purposes. After the 30-day trial, each agent subscribes individually — or you can choose to sponsor their seats directly.
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Total */}
            <div style={{
              background: colors.navy, borderRadius: 12, padding: 20, marginBottom: 24,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ color: '#8FA3BF', fontSize: 12, marginBottom: 4 }}>
                  {oneTimeTotal > 0 ? 'Due Today' : 'Monthly Total'}
                </div>
                <div style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>
                  {oneTimeTotal > 0 ? (
                    <>${oneTimeTotal.toLocaleString()}</>
                  ) : (
                    <>${monthlyTotal.toLocaleString()}<span style={{ fontSize: 14, color: '#8FA3BF', fontWeight: 400 }}>/mo</span></>
                  )}
                </div>
                {oneTimeTotal > 0 && (
                  <div style={{ color: colors.gold, fontSize: 12, fontWeight: 600 }}>
                    Then ${monthlyTotal.toLocaleString()}/mo starting in 30 days
                  </div>
                )}
              </div>
              <button onClick={() => { if (monthlyTotal > 0) setStep('info'); else setError('Select at least one product'); }}
                style={{ padding: '14px 32px', fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy }}>
                Continue <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 6 }} />
              </button>
            </div>

            {/* What's Included Summary */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e9ecef', marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.navy, marginBottom: 12 }}>What you get:</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                {[
                  ...(pipeline ? ['Kanban deal pipeline', 'Drag-and-drop stages', 'Lead source tracking', 'Borrower detail + notes'] : []),
                  ...(pipelineAdmin ? ['Processor seat — view & manage pipeline'] : []),
                  ...(coaching ? ['AI coaching prompts', 'Agent production metrics', 'Weekly call planner', 'Strategy chat', '4 coaching seats included', '1 free reporting admin'] : []),
                  ...(dataMigration ? ['Full data import from spreadsheets/CRM', '30-day implementation timeline'] : []),
                  ...(agentOnboarding ? [`${agentCount} agents onboarded with AI classification`, 'Agents get 30 days free trial', 'Agents opt in to share production data for coaching'] : []),
                ].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#555' }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.gold, fontSize: 10 }} /> {f}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {step === 'info' && (
          <div style={{ maxWidth: 500, margin: '0 auto' }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 32, border: '1px solid #e9ecef' }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.navy, margin: '0 0 4px' }}>
                <FontAwesomeIcon icon={faCreditCard} style={{ color: colors.gold, marginRight: 8 }} />
                Complete Your Signup
              </h3>
              <p style={{ fontSize: 13, color: colors.muted, margin: '0 0 8px' }}>
                {oneTimeTotal > 0 ? `$${oneTimeTotal.toLocaleString()} today` : ''}{oneTimeTotal > 0 && monthlyTotal > 0 ? ' + ' : ''}
                {monthlyTotal > 0 ? `$${monthlyTotal.toLocaleString()}/month${oneTimeTotal > 0 ? ' starting in 30 days' : ''}` : ''}
                <button onClick={() => setStep('plan')} style={{ marginLeft: 8, background: 'none', border: 'none', color: colors.gold, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Change</button>
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Company Name *</label>
                  <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="Your Company Name" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Contact Name *</label>
                  <input value={form.contactName} onChange={e => setForm({ ...form, contactName: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="John Smith" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Email *</label>
                  <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email"
                    style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="john@company.com" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Phone (optional)</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="(555) 123-4567" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Organization Type</label>
                  <select value={form.sponsorType} onChange={e => setForm({ ...form, sponsorType: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', background: '#fff', boxSizing: 'border-box' }}>
                    <option value="lender">Lender / Mortgage Company</option>
                    <option value="brokerage">Brokerage</option>
                    <option value="title">Title Company</option>
                    <option value="coach">Real Estate Coach</option>
                    <option value="vendor">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Password *</label>
                  <input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} type="password"
                    style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="Min 8 characters" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: colors.navy, display: 'block', marginBottom: 4 }}>Confirm Password *</label>
                  <input value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} type="password"
                    style={{ width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8, border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box' }}
                    placeholder="Re-enter password" />
                </div>
              </div>

              {/* RESPA Acknowledgment */}
              {form.sponsorType === 'lender' && (
                <div style={{ marginTop: 20, background: '#f0f7ff', borderRadius: 8, padding: 14, borderLeft: `3px solid #1565c0` }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 12, color: '#333', lineHeight: 1.6 }}>
                    <input type="checkbox" checked={respaAck} onChange={e => setRespaAck(e.target.checked)}
                      style={{ marginTop: 3, flexShrink: 0 }} />
                    <span>
                      <strong>RESPA Compliance Acknowledgment:</strong> I acknowledge that this platform provides software access only.
                      No marketing services, lead generation, or referral incentives are provided. Sponsored agents are under no obligation
                      to refer mortgage business or any settlement service business to my organization. This coaching program does not create
                      any referral arrangement or quid pro quo relationship.
                    </span>
                  </label>
                </div>
              )}
              {form.sponsorType !== 'lender' && !respaAck && <>{(() => { if (!respaAck) setTimeout(() => setRespaAck(true), 0); return null; })()}</>}

              {/* Terms of Service */}
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                  <input type="checkbox" checked={termsAck} onChange={e => setTermsAck(e.target.checked)}
                    style={{ marginTop: 3, flexShrink: 0 }} />
                  <span>
                    I agree to the <a href="https://aisearchportal.com/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: colors.gold, fontWeight: 600 }}>Terms of Service</a>,{' '}
                    <a href="https://aisearchportal.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: colors.gold, fontWeight: 600 }}>Privacy Policy</a>, and{' '}
                    <a href="https://aisearchportal.com/ai-ethics" target="_blank" rel="noopener noreferrer" style={{ color: colors.gold, fontWeight: 600 }}>AI Ethics Policy</a>.
                  </span>
                </label>
              </div>

              {error && <div style={{ marginTop: 16, padding: 12, background: '#ffebee', borderRadius: 8, fontSize: 13, color: '#c62828' }}>{error}</div>}

              <button onClick={handleCheckout} disabled={loading}
                style={{ width: '100%', marginTop: 24, padding: '14px', fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  background: loading ? '#ccc' : `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                  color: colors.navy, opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Redirecting to payment...' : oneTimeTotal > 0
                  ? `Pay $${oneTimeTotal.toLocaleString()} Today — Start Now`
                  : `Start at $${monthlyTotal.toLocaleString()}/month`
                }
              </button>

              <div style={{ marginTop: 16, fontSize: 11, color: '#888', textAlign: 'center', lineHeight: 1.6 }}>
                <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 4 }} />
                Secure payment via Stripe. Month-to-month. Cancel anytime.
                {form.sponsorType === 'lender' && <><br />RESPA compliant — software access only, no referral obligations.</>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorSignupPage;
